import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button/Button';
import Input from '../components/Input/Input';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

const ForgotPasswordWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.background.secondary};
`;

const ForgotPasswordCard = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.lg};
`;

const ForgotPasswordHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const ForgotPasswordTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ForgotPasswordSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.5;
`;

const ForgotPasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ForgotPasswordFooter = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const BackToSignIn = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: ${props => props.theme.fontWeight.medium};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.error}08;
  border: 1px solid ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.success}08;
  border: 1px solid ${props => props.theme.colors.success}20;
  color: ${props => props.theme.colors.success};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await forgotPassword(data.email);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <ForgotPasswordWrapper>
        <ForgotPasswordCard>
          <ForgotPasswordHeader>
            <ForgotPasswordTitle>Check Your Email</ForgotPasswordTitle>
            <ForgotPasswordSubtitle>
              We've sent a password reset link to your email address. 
              Please check your inbox and follow the instructions to reset your password.
            </ForgotPasswordSubtitle>
          </ForgotPasswordHeader>

          <ForgotPasswordFooter>
            <BackToSignIn>
              <Link to="/signin">← Back to Sign In</Link>
            </BackToSignIn>
          </ForgotPasswordFooter>
        </ForgotPasswordCard>
      </ForgotPasswordWrapper>
    );
  }

  return (
    <ForgotPasswordWrapper>
      <ForgotPasswordCard>
        <ForgotPasswordHeader>
          <ForgotPasswordTitle>Forgot Password</ForgotPasswordTitle>
          <ForgotPasswordSubtitle>
            Enter your email address and we'll send you a link to reset your password.
          </ForgotPasswordSubtitle>
        </ForgotPasswordHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ForgotPasswordForm onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^\S+@\S+$/i,
                message: 'Please enter a valid email',
              },
            })}
          />

          <Button
            type="submit"
            fullWidth
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="20px" showText={false} /> : 'Send Reset Link'}
          </Button>
        </ForgotPasswordForm>

        <ForgotPasswordFooter>
          <BackToSignIn>
            <Link to="/signin">← Back to Sign In</Link>
          </BackToSignIn>
        </ForgotPasswordFooter>
      </ForgotPasswordCard>
    </ForgotPasswordWrapper>
  );
};

export default ForgotPasswordPage;